<template>
  <div class="w-full overflow-x-hidden overflow-y:scroll z-0" @wheel="handleWheel">
    <div :style="gridStyle" ref="grid z-0">
      <PlanningHeader
        :slotsForADay="slotsForADay"
        :slotInterval="slotInterval"
        :hourMaxTime="hourMaxTime"
        :minuteWidth="minuteWidth"
      />

      <PlanningRow v-for="row in state.selectedRows.rows" :key="row.id" 
        ref="planningRow"
        :row="row"
        :slotsForADay="slotsForADay"
        :eventsByDay="eventsByDay"
        :minuteWidth="minuteWidth"
        :slotInterval="slotInterval"
        @open-event-popup="onOpenEventPopup($event)"
      />
    </div>

    <PopupEvent
      :event="popupEvent"
      :visible="popupEventVisible" 
      :byIdea="false"
      :project="state.project"
      @close="popupEventVisible = false"
      @delete="handleDeleteEvent($event)"
      @add="updateCacheForEvent($event); $emit('rows-update')"
      @update="$emit('rows-update')"
    />
  </div>
</template>

<script>
import PlanningHeader from './Rows/PlanningHeader.vue';
import PlanningRow from './Rows/PlanningRow.vue';

import PopupEvent from '../Popups/PopupEvent.vue';

import { deleteEvent, updateEvent } from '@/services/eventService';

import { inject } from 'vue';

export default {
  name: "PlanningGrid",
  components: {
    PlanningRow,
    PopupEvent,
    PlanningHeader
  },
  emits: ['rows-update'],
  setup() {
    const state = inject('state');
    const dateArrays = inject('dateArrays');
    const parameters = inject('parameters');
    const checkForViewChange = inject('checkForViewChange');
    const events = inject('events');
    return { state, dateArrays, parameters, checkForViewChange, events };
  },
  data() {
    return {
      hourMaxTime: 60,
      slotInterval: 15,
      windowWidth: window.innerWidth,
      eventsCache: {},
      eventsDaysMapping: {},
      popupEventVisible: false,
      popupEvent: null,
    };
  },
  computed: {
    // #region Grid and Slots
    gridStyle() {
      const columns = ['200px']; // colonne fixe pour l'en-tête (ex. la ressource)
      
      for (let i = 0; i < this.dateArrays.days.length; i++) {
        this.slotsForADay.forEach(s => {
          columns.push((this.minuteWidth * s.duration) + 'px');
        });
      }
      
      return {
        display: "grid",
        width: "100%",
        gridTemplateColumns: columns.join(' '),
        gridTemplateRows: `auto auto repeat(${this.state.selectedRows.rows.length}, auto)`
      };
    },
    slotsForADay(){
      const startMinute = this.parameters.night ? 0 : this.parameters.startMinute;
      const endMinute = this.parameters.night ? 60 * 24 : this.parameters.endMinute;
      const slots = [];
      let newInterval = this.slotInterval;
      
      // Si le slot fait une seule journée
      if(this.slotInterval == 60 * 24){
        newInterval = endMinute - startMinute
        slots.push({startMinute: this.parameters.startMinute, endMinute: this.parameters.endMinute, active: true, duration: newInterval});
        return slots;
      }

      let actMinute = startMinute;
      while (actMinute < endMinute) {
        const slot = {startMinute: actMinute, endMinute: actMinute, active: true, duration: newInterval};

        // Si le slot est en dehors des heures de travail, le marquer comme inactif
        if(actMinute < this.parameters.startMinute || actMinute >= this.parameters.endMinute) slot.active = false;

        if(actMinute % newInterval != 0){
          slot.endMinute = actMinute + (newInterval - (actMinute % newInterval));
          slot.duration = newInterval - (actMinute % newInterval);
          actMinute += (newInterval - (actMinute % newInterval));
        }
        else if(actMinute + newInterval > this.parameters.startMinute && actMinute < this.parameters.startMinute){
          actMinute = this.parameters.startMinute;
          slot.endMinute = this.parameters.startMinute;
        }
        else if(actMinute + newInterval > this.parameters.endMinute && actMinute < this.parameters.endMinute){
          actMinute = this.parameters.endMinute;
          slot.endMinute = this.parameters.endMinute;
        }
        else{
          actMinute += newInterval;
          slot.endMinute = actMinute;
        }

        
        slot.duration = slot.endMinute - slot.startMinute;
        slots.push(slot);
      }

      slots[slots.length - 1].endMinute = endMinute;
      slots[slots.length - 1].duration = slots[slots.length - 1].endMinute - slots[slots.length - 1].startMinute;
      
      return slots;
    },
    calculateSlotsProperties() {
      const dateDiff = this.state.endDate - this.state.startDate;
      const days = Math.round(dateDiff / (1000 * 60 * 60 * 24)) + 1;
      
      const config = [
        { maxDays: 1, slotInterval: 15, hourMaxTime: 60 },
        { maxDays: 2, slotInterval: 30, hourMaxTime: 120 },
        { maxDays: 3, slotInterval: 30, hourMaxTime: 180 },
        { maxDays: 4, slotInterval: 60, hourMaxTime: 240 },
        { maxDays: 5, slotInterval: 60, hourMaxTime: 300 },
        { maxDays: 6, slotInterval: 60, hourMaxTime: 360 },
        { maxDays: 14, slotInterval: 120, hourMaxTime: 360 },
        { maxDays: 33, slotInterval: 180, hourMaxTime: 360 },
        { maxDays: 60, slotInterval: 60 * 24, hourMaxTime: 360 },
        { maxDays: 90, slotInterval: 60 * 24, hourMaxTime: 360 },
        { maxDays: 180, slotInterval: 60 * 24, hourMaxTime: 360 },
        { maxDays: 365, slotInterval: 60 * 24, hourMaxTime: 360 },
        { maxDays: Infinity, slotInterval: 60 * 24, hourMaxTime: 360 }
      ];

      const selectedConfig = config.find(cfg => days <= cfg.maxDays);
      this.slotInterval = selectedConfig.slotInterval;
      this.hourMaxTime = selectedConfig.hourMaxTime;
    },
    minuteWidth() {
      const bodyPadding = 32;
      const gridWidth = this.windowWidth - bodyPadding - 16; // Utilise la largeur de la fenêtre
      // Calcul de la largeur disponible pour les slots actifs
      const availableActiveWidthPx = gridWidth - 200;
      const minutes = this.parameters.night ? 60 * 24 : this.parameters.endMinute - this.parameters.startMinute;

      return availableActiveWidthPx / (minutes * this.dateArrays.days.length);
    },
    // #endregion
    eventsByDay() {
      this.dateArrays.days.forEach(day => {
        const key = this.dayToKey(day);
        // Si le jour n'est pas encore dans le cache, le calculer
        if (!this.eventsCache[key]) {
          this.updateCacheForDay(day);
        }
      });
      return this.eventsCache;
    },
  },
  methods: {
    // #region Helpers
    onOpenEventPopup(event) {
      if(event.noEdit) return;

      this.popupEvent = event;
      this.popupEventVisible = true;
    },
    dayToKey(day){
      return day.getFullYear() + '-' +
        (day.getMonth() + 1).toString().padStart(2, '0') + '-' +
        day.getDate().toString().padStart(2, '0');
    },
    updateCacheForDay(day) {
      const key = this.dayToKey(day);
      // Réinitialiser le cache pour ce jour
      const segments = [];
      // Normaliser le jour de comparaison
      const normalizedDay = new Date(day.getFullYear(), day.getMonth(), day.getDate());
      
      this.events.forEach(event => {
        const specificId = "EzHknHJfunBYUXcCPvwb";

        if(event.id == specificId) {
          console.log(`Found specific event with id ${specificId}`);
        }

        const startDate = new Date(event.startDate);
        const endDate = new Date(event.endDate);
        const duration = (endDate - startDate) / (1000 * 60);

        if(event.id == specificId) {
          console.log(event.startDate);
        }
        
        // Normaliser les jours de l'événement
        const eventStartDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const eventEndDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        
        const daysMinutesWidth = this.parameters.night ? 60 * 24 : this.parameters.endMinute - this.parameters.startMinute;

        const startMin = startDate.getHours() * 60 + startDate.getMinutes();
        const endMin = endDate.getHours() * 60 + endDate.getMinutes();

        let width = 0;
        const newStart = startMin < this.parameters.startMinute ? this.parameters.startMinute : startMin;
        const newEnd = endMin > this.parameters.endMinute ? this.parameters.endMinute : endMin;
        if (this.parameters.night) {
          width = endMin - startMin;
        } else {
          width = newEnd - newStart;
        }

        // Si le jour normalisé est compris dans l'événement
        if (normalizedDay >= eventStartDay && normalizedDay <= eventEndDay) {
          const segment = { ...event };
          segment.duration = duration;
          if (eventStartDay.getTime() === eventEndDay.getTime()) {
            // Événement sur un seul jour
            segment.left = this.parameters.night ? startMin : newStart - this.parameters.startMinute;
            segment.width = width;
            segment.firstDay = true;
            segment.lastDay = true;
          } else if (normalizedDay.getTime() === eventStartDay.getTime()) {
            // Premier jour d'un multi-jours
            const startMin = startDate.getHours() * 60 + startDate.getMinutes();
            segment.left = this.parameters.night ? startMin : newStart - this.parameters.startMinute;
            segment.width = this.parameters.night ? 1440 - startMin : this.parameters.endMinute - newStart;
            segment.firstDay = true;
            segment.lastDay = false;
          } else if (normalizedDay.getTime() === eventEndDay.getTime()) {
            // Dernier jour
            const endMin = endDate.getHours() * 60 + endDate.getMinutes();
            segment.left = 0;
            segment.width = this.parameters.night ? endMin : newEnd - this.parameters.startMinute;
            segment.firstDay = false;
            segment.lastDay = true;
          } else {
            // Milieu d'un multi-jours
            segment.left = 0;
            segment.width = daysMinutesWidth;
            segment.firstDay = false;
            segment.lastDay = false;
          }
          segments.push(segment);
        }
      });
      segments.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
      // Affecter le cache pour ce jour
      this.eventsCache[key] = segments;
    },
    updateCacheForEvent(updatedEvent) {
      const eventId = updatedEvent.id;
      
      // Récupérer l'ancienne plage à partir du mapping
      const oldDays = this.eventsDaysMapping[eventId] || [];
      
      // Déterminer la nouvelle plage à partir des nouvelles dates
      const startDate = new Date(updatedEvent.startDate);
      const endDate = new Date(updatedEvent.endDate);
      const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

      let newDays = [];
      let dayIterator = new Date(startDay);
      while (dayIterator <= endDay) {
        const key = this.dayToKey(dayIterator);
        newDays.push(key);
        // Recalcule la plage pour ce jour
        this.updateCacheForDay(new Date(dayIterator));
        dayIterator.setDate(dayIterator.getDate() + 1);
      }
      
      // Pour tous les jours de l'ancienne plage qui ne font plus partie de la nouvelle, recalculer le cache
      oldDays.forEach(oldKey => {
        if (newDays.indexOf(oldKey) === -1) {
          const dayParts = oldKey.split('-');
          const oldDay = new Date(dayParts[0], parseInt(dayParts[1], 10) - 1, dayParts[2]);
          this.updateCacheForDay(oldDay);
        }
      });
      
      // Met à jour le mapping pour cet événement
      this.eventsDaysMapping[eventId] = newDays;
    },
    // #endregion

    // #region Handlers
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    handleWheel(event) {
      event.preventDefault();

      const dayDiff = Math.floor((this.state.endDate - this.state.startDate) / (1000 * 60 * 60 * 24));
      let newDays = dayDiff;

      if (event.ctrlKey) {
        // Ctrl : zoom classique
        if (event.deltaY < 0) {
          // Zoom in
          newDays = Math.max(0, dayDiff - Math.floor(1 + this.dateArrays.days.length / 10));
        } else if (event.deltaY > 0) {
          // Zoom out
          newDays = Math.min(368, dayDiff + Math.floor(1 + this.dateArrays.days.length / 10));
        }
        this.state.endDate = new Date(this.state.startDate);
        this.state.endDate.setDate(this.state.startDate.getDate() + newDays);
        this.checkForViewChange();
        return;
      }

      // Sans Ctrl : slide (décale la plage de dates)
      const daysDoAdd = event.deltaY > 0 ? this.state.selectedView.add : -this.state.selectedView.add;
      this.state.startDate = new Date(this.state.startDate.setDate(this.state.startDate.getDate() + daysDoAdd));
      this.state.endDate = new Date(new Date(this.state.startDate).setDate(this.state.startDate.getDate() + dayDiff));
      this.checkForViewChange();
    },
    async handleDeleteEvent(event) {
      this.events = this.events.filter(e => e.id !== event.id);

      if(event.accessId){
        this.events = this.events.filter(e => e.id !== event.accessId);
      }

      this.updateCacheForEvent(event);
      await deleteEvent(event);
    },
    // #endregion
  },
  async mounted() {
    window.addEventListener('scroll', this.scrollListener);

    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    // Nettoyer le ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Supprimer l'écouteur de scroll
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
    }

    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    'state.startDate'(){
      this.calculateSlotsProperties;
    },
    'state.endDate'(){
      this.calculateSlotsProperties;
    },
    events: {
      handler(){
        this.dateArrays.days.forEach(day => {
          this.updateCacheForDay(day);
        });
      },
      deep: true
    },
    'parameters': {
      handler(){
        this.calculateSlotsProperties;
        this.dateArrays.days.forEach(day => {
          this.updateCacheForDay(day);
        });
      },
      deep: true
    },
    window: {
      handler(){
        this.handleResize();
      },
      deep: true
    },
  }
};
</script>